import { useState, useEffect } from "react";
import { Row, Col, Container } from "react-bootstrap";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import VehicleCard from "../VehicleCard";
import { scroller } from "react-scroll";
import FinancingCarousel from "./FinancingCarousel";
import useFinanciaciones from "../../config/useFinanciaciones";
import loading from "../../assets/loading.svg";
import { saveTrackingParams } from "../../utils/utmTracker";
import { buildRouteWithUtms, saveUtmParams } from "../../config/whatsappConfig";

const matchesModelSearch = (item, tipoKey, query) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  const info = item[tipoKey];
  const modelo = (info?.modelo || "").toLowerCase();
  const version = (info?.version || "").toLowerCase();
  return modelo.includes(term) || version.includes(term);
};

const VehicleList = ({ vehicles }) => {
  const [tipoSearch, setTipoSearch] = useState("todos");
  const [modelSearch, setModelSearch] = useState("");

  const history = useHistory();
  const location = useLocation();
  const { tipo } = useParams();

  const { financiaciones, loading: loadingFinanciaciones } = useFinanciaciones();

  useEffect(() => {
    if (
      tipo !== "todos" &&
      tipo !== "convencional" &&
      tipo !== "plan" &&
      tipo !== "preventa" &&
      tipo !== "adjudicado" &&
      tipo !== "adjudicados" &&
      tipo !== undefined
    ) {
      saveTrackingParams();
      saveUtmParams();
      history.push(buildRouteWithUtms("/todos", location.search));
    }
  }, [history, tipo]);

  useEffect(() => {
    setTipoSearch(tipo === undefined ? "todos" : tipo);
  }, [tipo]);

  const filtradosConvencional = vehicles
    .filter((item) => item?.convencional?.habilitado === true)
    .filter((item) => matchesModelSearch(item, "convencional", modelSearch));

  const filtradosPlan = vehicles
    .filter((item) => item?.plan?.habilitado === true)
    .filter((item) => matchesModelSearch(item, "plan", modelSearch));

  const hasAdjudicados = vehicles.some((item) => item?.adjudicado?.habilitado === true);

  const filtradosAdjudicados = vehicles
    .filter((item) => item?.adjudicado?.habilitado === true)
    .filter((item) => matchesModelSearch(item, "adjudicado", modelSearch));

  const isSearching = Boolean(modelSearch.trim());

  const showConvencional =
    (tipoSearch === "convencional" || tipoSearch === "todos" || isSearching) &&
    (!isSearching || filtradosConvencional.length > 0);

  const showPlan =
    (tipoSearch === "plan" || tipoSearch === "todos" || isSearching) && (!isSearching || filtradosPlan.length > 0);

  const showAdjudicados =
    (tipoSearch === "adjudicados" || tipoSearch === "todos") && filtradosAdjudicados.length > 0 && !isSearching;

  const noSearchResults = isSearching && filtradosConvencional.length === 0 && filtradosPlan.length === 0;

  const handleTipo = (newTipo) => {
    saveTrackingParams();
    saveUtmParams();
    history.push(buildRouteWithUtms(`/${newTipo}`, location.search));
    setTipoSearch(newTipo);
    setTimeout(() => {
      scroller.scrollTo("scroll-click", { smooth: true, duration: 500, offset: -10 });
    }, 100);
  };

  const renderVehicleGrid = (items, nombreTipo, sortKey) => {
    if (!items.length) {
      return <p className="text-center pt-5 pb-5">Proximamente más opciones disponibles.</p>;
    }

    return items
      .sort((a, b) => a[sortKey].prioridad - b[sortKey].prioridad)
      .map((item, index) => (
        <Col lg={4} md={6} xs={12} key={index} className="flex-wrap mb-4">
          <VehicleCard
            info={item}
            tipo={tipoSearch}
            filtrados={items}
            nombreTipo={nombreTipo}
            todosLosVehiculos={vehicles}
          />
        </Col>
      ));
  };

  return (
    <>
      <div id="scroll-click" className="vehicle-list-toolbar pt-4 pb-4">
        <div className="vehicle-list-toolbar-inner">
          <div className="vehicle-list-toolbar-buttons">
            <button
              type="button"
              className={`btn btn-blue m-2 ${
                tipoSearch !== "plan" && tipoSearch !== "convencional" && tipoSearch !== "adjudicados" && "active"
              }`}
              onClick={() => handleTipo("todos")}
            >
              Ver todo
            </button>
            <button
              type="button"
              className={`btn btn-blue m-2 ${tipoSearch === "convencional" && "active"}`}
              onClick={() => handleTipo("convencional")}
            >
              Venta directa
            </button>
            <button
              type="button"
              className={`btn btn-blue m-2 ${tipoSearch === "plan" && "active"}`}
              onClick={() => handleTipo("plan")}
            >
              Fiat Plan
            </button>
            {hasAdjudicados && (
              <button
                type="button"
                className={`btn btn-blue m-2 ${tipoSearch === "adjudicados" && "active"}`}
                onClick={() => handleTipo("adjudicados")}
              >
                Adjudicados
              </button>
            )}
          </div>
          <div className="vehicle-list-search">
            <FiSearch aria-hidden />
            <input
              type="search"
              placeholder="Buscar modelo..."
              value={modelSearch}
              onChange={(e) => setModelSearch(e.target.value)}
              aria-label="Buscar modelo"
            />
            {modelSearch ? (
              <button type="button" onClick={() => setModelSearch("")} aria-label="Limpiar búsqueda">
                <FiX />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {noSearchResults ? (
        <p className="vehicle-list-no-results">No encontramos vehículos que coincidan con tu búsqueda.</p>
      ) : null}

      {showConvencional ? (
        <div id="convencional">
          <div className="vehicle-list-section-header">
            <Container>
              <Row>
                <Col>
                  <h2 className="vehicle-list-section-title text-center">
                    ¡Llevate tu vehículo en Montironi por <b>Venta Directa</b>!
                  </h2>
                </Col>
              </Row>
            </Container>
          </div>
          <Row className="d-flex">{renderVehicleGrid(filtradosConvencional, "convencional", "convencional")}</Row>
          <div id="financiaciones" className="financing-section">
            {loadingFinanciaciones ? (
              <>
                <p className="financing-section-subtitle">Financiá por Venta Directa</p>
                <Row className="d-flex justify-content-center mb-5">
                  <img src={loading} alt="Cargando..." style={{ width: 100, height: 100 }} />
                </Row>
              </>
            ) : financiaciones && financiaciones.length > 0 ? (
              <FinancingCarousel financiaciones={financiaciones} />
            ) : null}
          </div>
        </div>
      ) : null}

      {showPlan ? (
        <div className={tipoSearch === "plan" ? null : "mt-5"} id="plan">
          <div className="vehicle-list-section-header">
            <Container>
              <Row>
                <Col>
                  <h2 className="vehicle-list-section-title text-center">
                    ¡Tu 0km con <b>Fiat Plan</b> en Montironi!
                  </h2>
                </Col>
              </Row>
            </Container>
          </div>
          <Row className="d-flex">{renderVehicleGrid(filtradosPlan, "plan", "plan")}</Row>
        </div>
      ) : null}

      {showAdjudicados ? (
        <div className="mt-5" id="adjudicados">
          <div className="vehicle-list-section-header">
            <Container>
              <Row>
                <Col>
                  <h2 className="vehicle-list-section-title text-center">Planes adjudicados</h2>
                </Col>
              </Row>
            </Container>
          </div>
          <Row className="d-flex">{renderVehicleGrid(filtradosAdjudicados, "adjudicado", "adjudicado")}</Row>
        </div>
      ) : null}
    </>
  );
};

export default VehicleList;

